import React, { useState, useEffect } from 'react';
import {
  Store,
  ExternalLink,
  RefreshCw,
  Package,
  DollarSign,
  ShoppingBag,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Star,
  Bell,
  BarChart3,
  User
} from 'lucide-react';
import { getVendorToken, getVendorSession } from '../../services/myshopAuth';

interface DashboardData {
  vendor: {
    name: string;
    store_name: string;
    avatar_url: string;
    package: string;
  };
  orders: {
    total: number;
    pending: number;
    processing: number;
    completed: number;
    recent: Array<{
      id: number;
      status: string;
      total: string;
      date: string;
      customer: string;
    }>;
  };
  earnings: {
    available: string;
    pending: string;
    currency: string;
    connected: boolean;
  };
  products: {
    total: number;
    published: number;
    draft: number;
  };
  reviews: {
    rating: number;
    count: number;
  };
  profile: {
    percentage: number;
    missing: string[];
  };
  subscription: {
    package: string;
    status: string;
    expires: string | null;
  };
  announcements: {
    total: number;
    unread: number;
  };
  store_url: string | null;
  dashboard_url: string;
}

interface VendorDashboardProps {
  vendorId?: string;
}

const YB_DASHBOARD_URL = 'https://app.yourbeans.com.au';
// Use Vercel proxy to avoid CORS issues
const DASHBOARD_PROXY_URL = '/api/vendor-dashboard';

const VendorDashboard: React.FC<VendorDashboardProps> = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<DashboardData | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const session = getVendorSession();

  const fetchDashboardData = async () => {
    const token = getVendorToken();
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    try {
      // Use Vercel proxy to avoid CORS issues
      const response = await fetch(
        `${DASHBOARD_PROXY_URL}?token=${encodeURIComponent(token)}`
      );
      const result = await response.json();

      if (result.success) {
        setData(result);
        setError(null);
      } else {
        setError(result.message || 'Failed to load dashboard');
      }
    } catch (err) {
      setError('Unable to connect. Please check your internet connection.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchDashboardData();
  };

  const openFullDashboard = () => {
    window.open(`${YB_DASHBOARD_URL}/dashboard`, '_blank');
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-4 flex flex-col items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
        <p className="text-gray-600 mt-3 text-sm">Loading your dashboard...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-4 flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
        <h3 className="font-semibold text-gray-900 mb-2">Unable to load dashboard</h3>
        <p className="text-gray-600 text-sm text-center mb-4">{error}</p>
        <button
          onClick={handleRefresh}
          className="flex items-center px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 transition-colors"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Try Again
        </button>
      </div>
    );
  }

  if (!data) return null;

  const { orders, earnings, products, reviews, profile, subscription, announcements } = data;

  // Star rating component
  const StarRating = ({ rating }: { rating: number }) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-3.5 h-3.5 ${star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
        />
      ))}
    </div>
  );

  return (
    <div className="p-4 pb-24">
      {/* Header with subscription badge */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-full p-2">
            <Store className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="font-bold text-gray-900">{session?.storeName || 'Your Store'}</h2>
            <div className="flex items-center gap-2">
              <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">
                {subscription?.package || 'Free'}
              </span>
              {reviews?.rating > 0 && <StarRating rating={reviews.rating} />}
            </div>
          </div>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
        >
          <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Profile completion bar */}
      {profile && profile.percentage < 100 && (
        <div className="bg-blue-50 rounded-xl p-3 mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-900">Profile {profile.percentage}% complete</span>
            <User className="w-4 h-4 text-blue-500" />
          </div>
          <div className="w-full bg-blue-200 rounded-full h-2">
            <div
              className="bg-blue-500 h-2 rounded-full transition-all"
              style={{ width: `${profile.percentage}%` }}
            />
          </div>
          {profile.missing.length > 0 && (
            <p className="text-xs text-blue-700 mt-1">
              Add: {profile.missing.slice(0, 2).join(', ')}
            </p>
          )}
        </div>
      )}

      {/* Announcements alert */}
      {announcements && announcements.unread > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 flex items-center gap-3">
          <Bell className="w-5 h-5 text-amber-600" />
          <div className="flex-1">
            <span className="text-sm font-medium text-amber-900">
              {announcements.unread} new announcement{announcements.unread > 1 ? 's' : ''}
            </span>
          </div>
        </div>
      )}

      {/* Earnings Card */}
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 rounded-2xl p-4 mb-4 text-white">
        <div className="flex items-center gap-2 mb-1">
          <DollarSign className="w-4 h-4 opacity-80" />
          <span className="text-sm opacity-80">Available Balance</span>
        </div>
        <div className="text-3xl font-bold mb-3">
          ${earnings.available}
          <span className="text-base font-semibold opacity-80 ml-1">{earnings.currency}</span>
        </div>
        {earnings.connected ? (
          <div className="flex gap-4 text-sm">
            <div>
              <span className="opacity-70">Pending</span>
              <div className="font-semibold">${earnings.pending}</div>
            </div>
          </div>
        ) : (
          <p className="text-sm opacity-80">
            Set up payouts in your dashboard to start receiving your balance.
          </p>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {/* Orders */}
        <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
          <div className="flex items-center gap-1 text-gray-500 mb-1">
            <ShoppingBag className="w-3.5 h-3.5" />
            <span className="text-xs">Orders</span>
          </div>
          <div className="text-xl font-bold text-gray-900">{orders.total}</div>
          {orders.processing > 0 && (
            <div className="text-xs text-amber-600 font-medium">
              {orders.processing} processing
            </div>
          )}
        </div>

        {/* Products */}
        <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
          <div className="flex items-center gap-1 text-gray-500 mb-1">
            <Package className="w-3.5 h-3.5" />
            <span className="text-xs">Products</span>
          </div>
          <div className="text-xl font-bold text-gray-900">{products.published}</div>
          {products.draft > 0 && (
            <div className="text-xs text-gray-500">
              {products.draft} draft
            </div>
          )}
        </div>

        {/* Pending */}
        <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
          <div className="flex items-center gap-1 text-gray-500 mb-1">
            <Clock className="w-3.5 h-3.5" />
            <span className="text-xs">Pending</span>
          </div>
          <div className="text-xl font-bold text-gray-900">{orders.pending}</div>
          {orders.pending > 0 && (
            <div className="text-xs text-orange-600 font-medium">
              Needs action
            </div>
          )}
        </div>
      </div>

      {/* Recent Orders */}
      {orders.recent && orders.recent.length > 0 && (
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-900">Recent Orders</h3>
            <TrendingUp className="w-4 h-4 text-gray-400" />
          </div>
          <div className="space-y-3">
            {orders.recent.slice(0, 3).map((order) => (
              <div key={order.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                <div>
                  <div className="font-medium text-gray-900 text-sm">#{order.id}</div>
                  <div className="text-xs text-gray-500">{order.customer}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold text-gray-900 text-sm">${parseFloat(order.total).toFixed(2)}</div>
                  <div className={`text-xs font-medium ${
                    order.status === 'completed' ? 'text-green-600' :
                    order.status === 'processing' ? 'text-amber-600' :
                    'text-gray-500'
                  }`}>
                    {order.status === 'completed' && <CheckCircle className="w-3 h-3 inline mr-1" />}
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="grid grid-cols-4 gap-2 mb-4">
        <a
          href={`${YB_DASHBOARD_URL}/products`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center p-3 bg-white rounded-xl shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors"
        >
          <Package className="w-5 h-5 text-gray-600 mb-1" />
          <span className="text-xs text-gray-600">Products</span>
        </a>
        <a
          href={`${YB_DASHBOARD_URL}/orders`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center p-3 bg-white rounded-xl shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors"
        >
          <ShoppingBag className="w-5 h-5 text-gray-600 mb-1" />
          <span className="text-xs text-gray-600">Orders</span>
        </a>
        <a
          href={data.store_url || `${YB_DASHBOARD_URL}/site`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center p-3 bg-white rounded-xl shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors"
        >
          <Store className="w-5 h-5 text-gray-600 mb-1" />
          <span className="text-xs text-gray-600">Store</span>
        </a>
        <a
          href={`${YB_DASHBOARD_URL}/dashboard`}
          target="_blank"
          rel="noopener noreferrer"
          className="flex flex-col items-center p-3 bg-white rounded-xl shadow-sm border border-gray-100 hover:bg-gray-50 transition-colors"
        >
          <BarChart3 className="w-5 h-5 text-gray-600 mb-1" />
          <span className="text-xs text-gray-600">Dashboard</span>
        </a>
      </div>

      {/* Open Full Dashboard Button */}
      <button
        onClick={openFullDashboard}
        className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gray-900 text-white rounded-xl font-semibold hover:bg-gray-800 transition-colors"
      >
        <ExternalLink className="w-5 h-5" />
        Open Full Dashboard
      </button>
      <p className="text-center text-xs text-gray-500 mt-2">
        Settings, customization, and more
      </p>
    </div>
  );
};

export default VendorDashboard;
